import { enableProdMode } from "@angular/core";
import { platformBrowserDynamic } from "@angular/platform-browser-dynamic";

import {
  browserTracingIntegration,
  init,
  makeFetchTransport,
  makeMultiplexedTransport,
  moduleMetadataIntegration,
  replayIntegration
} from "@sentry/angular";
import { AppModule } from "./app/app.module";
import { EnvironmentType } from "./app/common/enums/environmentType";
import { AppConfig } from "./app/core/helpers/app.config";
import { environment } from "./environments/environment";

const enableSentry = () => {
  const environmentNameMap = {
    [EnvironmentType.PROD]: "production",
    [EnvironmentType.RC]: "staging",
    [EnvironmentType.DEV]: "development"
  };

  const EXTRA_KEY = "ROUTE_TO";
  const transport = makeMultiplexedTransport(makeFetchTransport, (args) => {
    const event = args.getEvent();
    if (event && event.extra && EXTRA_KEY in event.extra && Array.isArray(event.extra[EXTRA_KEY])) {
      return event.extra[EXTRA_KEY];
    }
    return [];
  });

  init({
    dsn: "https://46415e6b4671d3e079cb88af537e40b9@o4505917412343808.ingest.sentry.io/4506157169704960",
    environment: environmentNameMap[environment.type],
    release: environment.buildId,
    integrations: [
      // Registers and configures the Tracing integration,
      // which automatically instruments your application to monitor its
      // performance, including custom Angular routing instrumentation
      browserTracingIntegration(),
      // Registers the Replay integration,
      // which automatically captures Session Replays
      replayIntegration(),
      moduleMetadataIntegration()
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    transport,
    beforeSend: (event) => {
      if (event?.tags?.dsn) {
        if (!event?.tags?.prod) {
          return null; // don't send event if not in prod
        }
        event.extra = {
          ...event.extra,
          [EXTRA_KEY]: [event?.tags]
        };
        return event;
      }

      if (event?.exception?.values?.[0]?.stacktrace?.frames) {
        const frames = event.exception.values[0].stacktrace.frames;
        // Find the last frame with module metadata containing a DSN
        const routeTo = frames
          .filter((frame) => frame.module_metadata && frame.module_metadata.dsn)
          .map((v) => v.module_metadata)
          .slice(-1); // using top frame only - you may want to customize this according to your needs

        if (routeTo.length) {
          if (!routeTo[0].prod) {
            return null; // don't send event if not in prod
          }
          event.extra = {
            ...event.extra,
            [EXTRA_KEY]: routeTo
          };
        }
      }

      return event;
    }
  });
};

// For better debugging
console.log("BuildId", environment.buildId);

if (environment.type === EnvironmentType.PROD) {
  enableProdMode();
}

if (environment.type !== EnvironmentType.DEV) {
  enableSentry();
}

AppConfig.getInstance()
  .loadConnections()
  .then(() => AppConfig.getInstance().loadConfigs())
  .then(() => {
    platformBrowserDynamic()
      .bootstrapModule(AppModule)
      .catch((err) => console.error(err));
  });
